// NOTE: never import from strategy-types.ts -- it will create a circular loop since we export from strategy-conditionals.ts to strategy-types.ts
// strategy-conditionals.ts
// Supports both EVM (0x...) and Solana (base58) addresses
type AssetIdFormat = `${string}:${string}`;

export enum AvailableTimeSeriesResolutions {
  // FOOTGUN(johnrjj) - Typescript doesn't allowed numeric name enums (e.g. '1' is invalid) so we had an underscore in front of the number.
  ONE_MINUTE = "1",
  FIVE_MINUTES = "5",
  FIFTEEN_MINUTES = "15",
  THIRTY_MINUTES = "30",
  ONE_HOUR = "60",
  FOUR_HOURS = "240",
  TWELVE_HOURS = "720",
  ONE_DAY = "1D",
  ONE_WEEK = "7D",
}

type FetchAssetInfo<TArgs, UReturn> = (
  assetIdArgs: TArgs & { assetId: AssetIdFormat | null },
) => Promise<UReturn>;

type AssetLookupDatapointTypeFunction<TArgs, UReturn> = (
  args: TArgs,
  assetFetchFn: FetchAssetInfo<TArgs, UReturn>,
) => Promise<number>; // note this is a number

interface ConditionRuntimeFnMetadata {
  name: string;
  shortName: string;
}

interface ConditionalFunctionDefinition<TArgs, UReturn> {
  id: string;
  fn: AssetLookupDatapointTypeFunction<TArgs, UReturn>;
  metadata: ConditionRuntimeFnMetadata;
  defaultArgs: TArgs;
  validateArgs: (args: Partial<TArgs>) => boolean;
}

const createConditionalFunction = <TArgs, UReturn>(
  id: string,
  fn: AssetLookupDatapointTypeFunction<TArgs, UReturn>,
  metadata: ConditionRuntimeFnMetadata,
  defaultArgs: TArgs,
  validateArgs: (args: Partial<TArgs>) => boolean,
): ConditionalFunctionDefinition<TArgs, UReturn> => ({
  id,
  fn,
  metadata,
  defaultArgs,
  validateArgs,
});

// Define conditional functions
export const conditionalFunctions = {
  price: createConditionalFunction<
    { assetId: AssetIdFormat | null; quoteAssetId?: AssetIdFormat | null },
    { priceUSD: string }
  >(
    "price",
    async (args, assetFetchFn) => {
      const asset = await assetFetchFn(args);
      return Number.parseFloat(asset.priceUSD);
    },
    { name: "Current Price", shortName: "Price" },
    { assetId: null, quoteAssetId: null },
    (args) => !!args.assetId, // && args.quoteAssetId !== null // TODO(johnrjj) - Add quoteAssetId to conditional functions
  ),
  marketCap: createConditionalFunction<
    { assetId: AssetIdFormat | null },
    { marketCap: string }
  >(
    "marketCap",
    async (args, assetFetchFn) => {
      const asset = await assetFetchFn(args);
      return Number.parseFloat(asset.marketCap);
    },
    { name: "Current Market Cap", shortName: "MCap" },
    { assetId: null },
    (args) => !!args.assetId,
  ),
  fixedValue: createConditionalFunction<
    { fixedValue: string; denomination: "usd" | "percent" },
    { fixedValue: string; denomination: "usd" | "percent" }
  >(
    "fixedValue",
    async ({ fixedValue }) => Number.parseFloat(fixedValue),
    { name: "Fixed Value", shortName: "Fixed" },
    { fixedValue: "", denomination: "usd" },
    (args) => !!args.fixedValue && !!args.denomination,
  ),
  volume: createConditionalFunction<
    {
      assetId: AssetIdFormat | null;
      resolution: AvailableTimeSeriesResolutions;
    },
    { volume: number }
  >(
    "volume",
    async (args, assetFetchFn) => {
      const asset = await assetFetchFn(args);
      return asset.volume;
    },
    { name: "Volume", shortName: "Vol" },
    {
      assetId: null,
      resolution: AvailableTimeSeriesResolutions.ONE_DAY,
    },
    (args) => !!args.assetId && !!args.resolution,
  ),
  sma: createConditionalFunction<
    {
      assetId: AssetIdFormat | null;
      windowSize: number;
      resolution: AvailableTimeSeriesResolutions;
    },
    { sma: number }
  >(
    "sma",
    async (args, assetFetchFn) => {
      const asset = await assetFetchFn(args);
      return asset.sma;
    },
    { name: "Simple Moving Average", shortName: "SMA" },
    {
      assetId: null,
      windowSize: 9,
      resolution: AvailableTimeSeriesResolutions.ONE_HOUR,
    },
    (args) => !!args.assetId && !!args.windowSize && !!args.resolution,
  ),
  ema: createConditionalFunction<
    {
      assetId: AssetIdFormat | null;
      windowSize: number;
      resolution: AvailableTimeSeriesResolutions;
    },
    { ema: number }
  >(
    "ema",
    async (args, assetFetchFn) => {
      const asset = await assetFetchFn(args);
      return asset.ema;
    },
    { name: "Exponential Moving Average", shortName: "EMA" },
    {
      assetId: null,
      windowSize: 14,
      resolution: AvailableTimeSeriesResolutions.ONE_HOUR,
    },
    (args) => !!args.assetId && !!args.windowSize && !!args.resolution,
  ),
} as const;

type ConditionalFunctions = typeof conditionalFunctions;
export type ConditionalFunctionId = keyof ConditionalFunctions;

const AVAILABLE_CONDITIONAL_FUNCTION_IDS = Object.keys(
  conditionalFunctions,
) as ConditionalFunctionId[];

const AVAILABLE_CONDITIONAL_FUNCTION_IDS_LEFT =
  AVAILABLE_CONDITIONAL_FUNCTION_IDS.filter(
    (id) => id !== "fixedValue",
  ) as Exclude<ConditionalFunctionId, "fixedValue">[];
const AVAILABLE_CONDITIONAL_FUNCTION_IDS_RIGHT =
  AVAILABLE_CONDITIONAL_FUNCTION_IDS;

type ConditionalAvailableRuntimeFnIdsLeft = Exclude<
  ConditionalFunctionId,
  "fixedValue"
>;
type ConditionalAvailableRuntimeFnIdsRight = ConditionalFunctionId;

const AVAILABLE_CONDITIONAL_FUNCTION_IDS_LEFT_CONST = [
  "price",
  "marketCap",
  "sma",
  "volume",
  "ema",
] as const;

const AVAILABLE_CONDITIONAL_FUNCTION_IDS_RIGHT_CONST = [
  ...AVAILABLE_CONDITIONAL_FUNCTION_IDS_LEFT_CONST,
  "fixedValue",
] as const;

type ConditionalAvailableRuntimeFnIdsWithoutFixed =
  (typeof AVAILABLE_CONDITIONAL_FUNCTION_IDS_LEFT_CONST)[number];
type ConditionalAvailableRunetimeFnIdsRight =
  (typeof AVAILABLE_CONDITIONAL_FUNCTION_IDS_RIGHT_CONST)[number];

// Helper type to get the function definition for a specific conditional function ID
type ConditionalFunctionDefinitionFor<T extends keyof ConditionalFunctions> =
  ConditionalFunctions[T];

function getConditionRuntimeFunctionObjectById<
  T extends keyof ConditionalFunctions,
>(fnId: T): ConditionalFunctionDefinitionFor<T> & { id: T } {
  return {
    ...conditionalFunctions[fnId],
    id: fnId,
  };
}

const generateDefaultArgsForFnId = <
  T extends ConditionalAvailableRunetimeFnIdsRight,
>(
  fnId: T,
): T extends ConditionalAvailableRuntimeFnIdsLeft
  ? CondLeftArgsNoRuntime
  : CondRightArgsNoRuntime => {
  const func = conditionalFunctions[fnId];
  return {
    id: fnId,
    args: func.defaultArgs,
  } as any; // Type assertion needed due to TypeScript limitations
};

const generateDefaultArgsForFnLeft = <
  T extends ConditionalAvailableRuntimeFnIdsLeft,
>(
  fnId: T,
): CondLeftArgsNoRuntime => {
  const func = conditionalFunctions[fnId];
  return {
    id: fnId,
    args: func.defaultArgs,
  } as CondLeftArgsNoRuntime;
};

const generateDefaultArgsForFnRight = <
  T extends ConditionalAvailableRunetimeFnIdsRight,
>(
  fnId: T,
): CondRightArgsNoRuntime => {
  const func = conditionalFunctions[fnId];
  return {
    id: fnId,
    args: func.defaultArgs,
  } as CondRightArgsNoRuntime;
};

const checkIfConditionalArgsSet = (
  args: { id: ConditionalFunctionId; args: any },
  ignoreAssetId = false,
): boolean => {
  if (!args || !args.id) return false;
  const func = conditionalFunctions[args.id];
  return ignoreAssetId
    ? func.validateArgs({ ...args.args, assetId: "dummy" as AssetIdFormat })
    : func.validateArgs(args.args);
};

const checkIfConditionalLeftArgsSet = checkIfConditionalArgsSet;
const checkIfConditionalRightArgsSet = checkIfConditionalArgsSet;

const AVAILABLE_COMPARISON_OPERATORS = ["greater", "less"] as const;
type ComparisonOperators = (typeof AVAILABLE_COMPARISON_OPERATORS)[number];

const AVAILABLE_COMPARISON_OPERATORS_LABELS = [
  "greater than",
  "less than",
] as const;

const getComparisonOperatorLabel = (
  operator: ComparisonOperators,
): (typeof AVAILABLE_COMPARISON_OPERATORS_LABELS)[number] => {
  return AVAILABLE_COMPARISON_OPERATORS_LABELS[
    AVAILABLE_COMPARISON_OPERATORS.indexOf(operator)
  ]!;
};

export type ExtractArgs<T> =
  T extends ConditionalFunctionDefinition<infer Args, any> ? Args : never;
type ExtractReturnData<T> =
  T extends ConditionalFunctionDefinition<any, infer Return> ? Return : never;

type CondLeftArgsNoRuntime =
  | { id: "price"; args: ExtractArgs<typeof conditionalFunctions.price> }
  | {
      id: "marketCap";
      args: ExtractArgs<typeof conditionalFunctions.marketCap>;
    }
  | { id: "sma"; args: ExtractArgs<typeof conditionalFunctions.sma> }
  | { id: "ema"; args: ExtractArgs<typeof conditionalFunctions.ema> }
  | { id: "volume"; args: ExtractArgs<typeof conditionalFunctions.volume> };

type CondRightArgsNoRuntime =
  | CondLeftArgsNoRuntime
  | {
      id: "fixedValue";
      args: ExtractArgs<typeof conditionalFunctions.fixedValue>;
    };

// For partial args (used in UI interactions)
type PartialArgs<T extends ConditionalFunctionId> = {
  id: T;
  args: Partial<ExtractArgs<(typeof conditionalFunctions)[T]>>;
};

type CondLeftArgsNoRuntimePartialArgs =
  PartialArgs<ConditionalAvailableRuntimeFnIdsLeft>;

type CondRightArgsNoRuntimePartialArgs =
  PartialArgs<ConditionalAvailableRuntimeFnIdsRight>;

// Update the type guard functions to check for the specific return properties
function isCondLeftArgsNoRuntime(obj: any): obj is CondLeftArgsNoRuntime {
  if (!obj || typeof obj.id !== "string" || typeof obj.args !== "object")
    return false;
  switch (obj.id) {
    case "price":
      // TODO(johnrjj) - Add quoteAssetId to conditional functions
      return "assetId" in obj.args;
    case "marketCap":
      return "assetId" in obj.args;
    case "sma":
    case "ema":
      return (
        "assetId" in obj.args &&
        "windowSize" in obj.args &&
        "resolution" in obj.args
      );
    case "volume":
      return "assetId" in obj.args && "resolution" in obj.args;
    default:
      return false;
  }
}

// Type guard function to check if an object is CondRightArgsNoRuntime
function isCondRightArgsNoRuntime(obj: any): obj is CondRightArgsNoRuntime {
  return (
    isCondLeftArgsNoRuntime(obj) ||
    (obj &&
      obj.id === "fixedValue" &&
      typeof obj.args === "object" &&
      "fixedValue" in obj.args &&
      "denomination" in obj.args)
  );
}
type CondLeftArgs =
  | {
      id: "price";
      args: ExtractArgs<typeof conditionalFunctions.price>;
      conditionalFnRuntime: typeof conditionalFunctions.price;
    }
  | {
      id: "marketCap";
      args: ExtractArgs<typeof conditionalFunctions.marketCap>;
      conditionalFnRuntime: typeof conditionalFunctions.marketCap;
    }
  | {
      id: "sma";
      args: ExtractArgs<typeof conditionalFunctions.sma>;
      conditionalFnRuntime: typeof conditionalFunctions.sma;
    }
  | {
      id: "ema";
      args: ExtractArgs<typeof conditionalFunctions.ema>;
      conditionalFnRuntime: typeof conditionalFunctions.ema;
    }
  | {
      id: "volume";
      args: ExtractArgs<typeof conditionalFunctions.volume>;
      conditionalFnRuntime: typeof conditionalFunctions.volume;
    }; // no fixed value

type CondRightArgs =
  | CondLeftArgs
  | {
      id: "fixedValue";
      args: ExtractArgs<typeof conditionalFunctions.fixedValue>;
      conditionalFnRuntime: typeof conditionalFunctions.fixedValue;
    };

export {
  conditionalFunctions as AVAILABLE_CONDITION_RUNTIME_FUNCTION_LIBRARY,
  AVAILABLE_CONDITIONAL_FUNCTION_IDS,
  AVAILABLE_CONDITIONAL_FUNCTION_IDS_LEFT,
  AVAILABLE_CONDITIONAL_FUNCTION_IDS_RIGHT,
  AVAILABLE_COMPARISON_OPERATORS,
  getConditionRuntimeFunctionObjectById,
  generateDefaultArgsForFnId,
  generateDefaultArgsForFnLeft,
  generateDefaultArgsForFnRight,
  checkIfConditionalArgsSet,
  getComparisonOperatorLabel,
  isCondLeftArgsNoRuntime,
  isCondRightArgsNoRuntime,
};

export type {
  ConditionRuntimeFnMetadata,
  ConditionalAvailableRuntimeFnIdsLeft,
  ConditionalAvailableRuntimeFnIdsRight,
  ConditionalAvailableRuntimeFnIdsWithoutFixed,
  ConditionalAvailableRunetimeFnIdsRight,
  ComparisonOperators,
  CondLeftArgsNoRuntime,
  CondRightArgsNoRuntime,
  CondLeftArgsNoRuntimePartialArgs,
  CondRightArgsNoRuntimePartialArgs,
  CondLeftArgs,
  CondRightArgs,
};

// Deprecated exports (to be removed in future versions)
/** @deprecated Use AVAILABLE_CONDITION_RUNTIME_FUNCTION_LIBRARY instead */
export const AVAILABLE_CONDITION_RUNTIME_FUNCTION_LIBRARY_AS_ARRAY =
  Object.values(conditionalFunctions);

/** @deprecated Access default args directly from AVAILABLE_CONDITION_RUNTIME_FUNCTION_LIBRARY */
export const DefaultCondPriceArgs = conditionalFunctions.price.defaultArgs;
/** @deprecated Access default args directly from AVAILABLE_CONDITION_RUNTIME_FUNCTION_LIBRARY */
export const DefaultCondMarketCapArgs =
  conditionalFunctions.marketCap.defaultArgs;
/** @deprecated Access default args directly from AVAILABLE_CONDITION_RUNTIME_FUNCTION_LIBRARY */
export const DefaultCondSimpleMovingAverageArgs =
  conditionalFunctions.sma.defaultArgs;

// Deprecated type exports (to be removed in future versions)
/** @deprecated Use CondLeftArgs or CondRightArgs instead */
export type CondArgsNoRuntime<T extends ConditionalFunctionId> = {
  id: T;
  args: Partial<Parameters<(typeof conditionalFunctions)[T]["fn"]>[0]>;
};

/** @deprecated Use type inference from AVAILABLE_CONDITION_RUNTIME_FUNCTION_LIBRARY instead */
export type CurrentMarketCapFnReturnData = ReturnType<
  typeof conditionalFunctions.marketCap.fn
>;
/** @deprecated Use type inference from AVAILABLE_CONDITION_RUNTIME_FUNCTION_LIBRARY instead */
export type SimpleMovingAverageFnReturnData = ReturnType<
  typeof conditionalFunctions.sma.fn
>;
/** @deprecated Use type inference from AVAILABLE_CONDITION_RUNTIME_FUNCTION_LIBRARY instead */
export type CurrentPriceFnReturnData = ReturnType<
  typeof conditionalFunctions.price.fn
>;
/** @deprecated Use type inference from AVAILABLE_CONDITION_RUNTIME_FUNCTION_LIBRARY instead */
export type FixedValueFnReturnData = ReturnType<
  typeof conditionalFunctions.fixedValue.fn
>;

/** @deprecated Use type inference from AVAILABLE_CONDITION_RUNTIME_FUNCTION_LIBRARY instead */
export type CurrentPriceFnArgs = Parameters<
  typeof conditionalFunctions.price.fn
>[0];
/** @deprecated Use type inference from AVAILABLE_CONDITION_RUNTIME_FUNCTION_LIBRARY instead */
export type CurrentMarketCapFnArgs = Parameters<
  typeof conditionalFunctions.marketCap.fn
>[0];
/** @deprecated Use type inference from AVAILABLE_CONDITION_RUNTIME_FUNCTION_LIBRARY instead */
export type SimpleMovingAverageArgs = Parameters<
  typeof conditionalFunctions.sma.fn
>[0];
/** @deprecated Use type inference from AVAILABLE_CONDITION_RUNTIME_FUNCTION_LIBRARY instead */
export type FixedValueFnArgs = Parameters<
  typeof conditionalFunctions.fixedValue.fn
>[0];
